import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, Loader2, RotateCcw, Printer } from 'lucide-react';

const PdfViewer = ({ dataUrl, orderId, onDownload }) => {
  const canvasRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const renderTaskRef = useRef(null);

  const handleSavePdf = () => {
    if (onDownload) onDownload();
  };

  const handlePrint = () => {
    if (!dataUrl) return;
    // Use iframe with srcdoc instead of document.write to avoid XSS
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const safeTitle = (orderId || 'Document').replace(/[<>"'&]/g, '');
    const html = `<html><head><title>Print - ${safeTitle}</title>
      <style>@media print { body { margin: 0; } img { width: 100%; height: auto; } }</style>
      </head><body>
      <embed src="${encodeURI(dataUrl)}" type="application/pdf" width="100%" height="100%" />
      </body></html>`;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.addEventListener('load', () => {
      setTimeout(() => printWindow.print(), 500);
    });
  };

  useEffect(() => {
    let cancelled = false;
    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);
        const pdfjsLib = await import('pdfjs-dist/build/pdf');
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

        const binary = atob(dataUrl.split(',')[1]);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        const doc = await pdfjsLib.getDocument({ data: bytes }).promise;
        if (!cancelled) {
          setPdf(doc);
          setTotal(doc.numPages);
          setPage(1);
        }
      } catch (e) {
        if (!cancelled) setError('Failed to load PDF');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (dataUrl) loadPdf();
    return () => { cancelled = true; };
  }, [dataUrl]);

  const renderPage = useCallback(async () => {
    if (!pdf || !canvasRef.current) return;

    if (renderTaskRef.current) {
      try { renderTaskRef.current.cancel(); } catch {}
      renderTaskRef.current = null;
    }

    try {
      const pg = await pdf.getPage(page);
      const viewport = pg.getViewport({ scale });
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const task = pg.render({ canvasContext: ctx, viewport });
      renderTaskRef.current = task;
      await task.promise;
      renderTaskRef.current = null;
    } catch (e) {
      if (e?.name !== 'RenderingCancelledException') {
        setError('Failed to render page');
      }
    }
  }, [pdf, page, scale]);

  useEffect(() => {
    renderPage();
  }, [renderPage]);

  const zoomIn = () => setScale(s => Math.min(3, +(s + 0.25).toFixed(2)));
  const zoomOut = () => setScale(s => Math.max(0.5, +(s - 0.25).toFixed(2)));
  const resetZoom = () => setScale(1.0);

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 300 }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">Loading invoice...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-4" style={{ height: 300 }}>
        <p className="text-red-500">{error}</p>
        {(onDownload || dataUrl) && (
          <Button onClick={handleSavePdf} data-testid="pdf-error-download-btn">
            <Download className="h-4 w-4 mr-2" /> Download Invoice PDF
          </Button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="invoice-pdf-viewer">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-100 border-b border-t gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="pdf-prev-page">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm px-2 text-slate-700" data-testid="pdf-page-info">{page} / {total}</span>
          <Button variant="outline" size="sm" disabled={page >= total} onClick={() => setPage(p => p + 1)} data-testid="pdf-next-page">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={zoomOut} disabled={scale <= 0.5} data-testid="pdf-zoom-out">
            <ZoomOut className="h-4 w-4" />
          </Button>
          <button
            onClick={resetZoom}
            className="text-sm px-2 text-slate-700 font-medium hover:text-sky-600 cursor-pointer min-w-[48px] text-center"
            title="Click to reset zoom to 100%"
            data-testid="pdf-zoom-level"
          >
            {Math.round(scale * 100)}%
          </button>
          <Button variant="outline" size="sm" onClick={zoomIn} disabled={scale >= 3} data-testid="pdf-zoom-in">
            <ZoomIn className="h-4 w-4" />
          </Button>
          {scale !== 1.0 && (
            <Button variant="outline" size="sm" onClick={resetZoom} className="ml-1" data-testid="pdf-zoom-reset">
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          )}
          {(onDownload || dataUrl) && (
            <>
              <Button variant="outline" size="sm" onClick={handlePrint} className="ml-2" data-testid="pdf-print-btn">
                <Printer className="h-4 w-4 mr-1" /> Print
              </Button>
              <Button variant="outline" size="sm" onClick={handleSavePdf} className="ml-1" data-testid="pdf-save-btn">
                <Download className="h-4 w-4 mr-1" /> Save
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Scrollable Canvas Container — explicit max-height ensures overflow triggers scrollbars */}
      <div
        data-testid="pdf-scroll-container"
        style={{
          maxHeight: 'calc(80vh - 100px)',
          overflow: 'auto',
          background: '#e2e8f0',
          textAlign: 'center',
          padding: '16px',
        }}
      >
        <canvas
          ref={canvasRef}
          className="shadow-lg bg-white inline-block"
        />
      </div>
    </div>
  );
};

export default PdfViewer;
